/** @jsxImportSource @emotion/react */
import { SerializedStyles, css } from "@emotion/react";
import { FunctionComponent, useMemo } from "react";

import { StrapiArticleBodyDynamicZone } from "@/types/strapi";
import { GridColumnCount, GridColumnSpan } from "@/types/tokens/grid";

import { BorderRadiuses } from "@/tokens/border";
import { Colors } from "@/tokens/color";
import { ButtonVariantKeys } from "@/tokens/configs/button_config";
import { ColumnGaps } from "@/tokens/spacing";

import { Button } from "@/ui/atoms/button";
import { Embed } from "@/ui/atoms/embed";
import { ExternalVideo } from "@/ui/atoms/external_video";
import { GridColumn } from "@/ui/atoms/grid_column";
import { Media } from "@/ui/atoms/media";
import { Quote } from "@/ui/atoms/quote";
import { Text } from "@/ui/atoms/text";
import { RichText } from "@/ui/molecules/rich_text";

import { buildStylesByBreakpoint } from "@/util/style_util";

interface RichTextBlockLayout {
    columnSpan?: GridColumnSpan;
    columnStart?: GridColumnCount;
}

interface ArticleBodyProps {
    Article_Body: StrapiArticleBodyDynamicZone;
    className?: SerializedStyles;
    mediaBlockLayout?: RichTextBlockLayout;
    shouldVideoLoop?: boolean;
    shouldVideoShowControls?: boolean;
    textBlockLayout?: RichTextBlockLayout;
}

export const ArticleBody: FunctionComponent<ArticleBodyProps> = (props) => {
    /**
     * Styles
     */
    const firstChildMarginTop = useMemo(
        () => (_index: number) =>
            _index === 0
                ? css({
                      "& div *:first-of-type": {
                          marginTop: 0,
                      },
                      marginTop: "0 !important",
                  })
                : undefined,
        [],
    );

    const textBlockWidth = useMemo(() => {
        const columnSpans = props.textBlockLayout?.columnSpan;
        if (!columnSpans) return {};

        const widths = Object.entries(columnSpans).reduce(
            (acc, [breakpoint, span]) => {
                acc[breakpoint] =
                    span === "full"
                        ? "100%"
                        : `calc(${span - 1} * (${ColumnGaps[breakpoint as keyof typeof ColumnGaps]}) + ${span} * (100% - 5 * ${ColumnGaps[breakpoint as keyof typeof ColumnGaps]}) / 6)`;
                return acc;
            },
            {} as Record<string, string>,
        );

        return css(buildStylesByBreakpoint("width", widths), {
            marginLeft: "auto",
            marginRight: "auto",
        });
    }, [props.textBlockLayout?.columnSpan]);

    /**
     * Rendering
     */
    return props.Article_Body.map((block, _index) => {
        switch (block.__component) {
            case "primitives.primitive-markdown-text":
                return (
                    <GridColumn
                        className={css(
                            firstChildMarginTop(_index),
                            textBlockWidth,
                        )}
                        key={`article_body::markdown::${block.id}-${_index}`}
                        {...props.textBlockLayout}
                    >
                        <RichText config="Article">{block.Text}</RichText>
                    </GridColumn>
                );
            case "primitives.primitive-quote":
                return (
                    <GridColumn
                        className={css(
                            firstChildMarginTop(_index),
                            textBlockWidth,
                        )}
                        key={`article_body::quote::${block.id}-${_index}`}
                        marginBottom="ArticleQuote"
                        marginTop="ArticleQuote"
                        {...props.textBlockLayout}
                    >
                        <Quote {...block} />
                    </GridColumn>
                );
            case "primitives.primitive-external-video":
                return (
                    <GridColumn
                        className={firstChildMarginTop(_index)}
                        key={`article_body::video::${block.id}-${_index}`}
                        marginBottom="ArticleLargeMargin"
                        marginTop="ArticleLargeMargin"
                        tag="figure"
                        {...props.mediaBlockLayout}
                    >
                        <ExternalVideo
                            className={css({
                                aspectRatio: block.aspectRatio,
                                backgroundColor: Colors["black"],
                                borderRadius: BorderRadiuses["borderMedium"],
                                overflow: "hidden",
                            })}
                            controls={props.shouldVideoShowControls}
                            loop={props.shouldVideoLoop}
                            url={block.URL}
                        />

                        {block.Caption && (
                            <Text
                                fontSize="Micro"
                                marginTop="ArticleCaption"
                                tag="figcaption"
                                textAlign="center"
                            >
                                <RichText config="ArticleCaption">
                                    {block.Caption}
                                </RichText>
                            </Text>
                        )}
                    </GridColumn>
                );

            case "primitives.primitive-media":
                return (
                    <GridColumn
                        className={firstChildMarginTop(_index)}
                        key={`article_body::media::${block.id}-${_index}`}
                        marginBottom="ArticleLargeMargin"
                        marginTop="ArticleLargeMargin"
                        tag="figure"
                        {...props.mediaBlockLayout}
                    >
                        <Media {...block} />

                        {block.Caption && (
                            <Text
                                fontSize="Micro"
                                marginTop="ArticleCaption"
                                tag="figcaption"
                                textAlign="center"
                            >
                                <RichText config="ArticleCaption">
                                    {block.Caption}
                                </RichText>
                            </Text>
                        )}
                    </GridColumn>
                );
            case "primitives.primitive-embed":
                return (
                    <GridColumn
                        className={firstChildMarginTop(_index)}
                        key={`article_body::embed::${block.id}-${_index}`}
                        marginBottom="ArticleLargeMargin"
                        marginTop="ArticleLargeMargin"
                        {...props.textBlockLayout}
                    >
                        <Embed className={css({ width: "100%" })} {...block} />

                        {block.Caption && (
                            <Text
                                fontSize="Micro"
                                marginTop="ArticleCaption"
                                tag="figcaption"
                                textAlign="center"
                            >
                                <RichText config="ArticleCaption">
                                    {block.Caption}
                                </RichText>
                            </Text>
                        )}
                    </GridColumn>
                );
            case "primitives.primitive-button":
                return (
                    <GridColumn
                        className={css(
                            {
                                display: "flex",
                                justifyContent: "center",
                            },
                            textBlockWidth,
                        )}
                        key={`article_body::button::${block.id}-${_index}`}
                        marginBottom="ArticleLargeMargin"
                        marginTop="ArticleLargeMargin"
                        {...props.textBlockLayout}
                    >
                        <Button
                            fontSize="ArticleButton"
                            href={block.URL}
                            variant={
                                block.Appearance.toLowerCase() as
                                    | ButtonVariantKeys
                                    | undefined
                            }
                        >
                            {block.Text}
                        </Button>
                    </GridColumn>
                );
            default:
                break;
        }
    });
};
